/**
 * Owner-supplied regulatory doctrine sources (dated catalog).
 *
 * These entries record items the owner researched for the 5 October 2026
 * regulatory review. They feed doctrine and guard text ONLY. They never set a
 * site's status, are never attached to an issue row as site evidence, and are
 * never treated as verified law: every entry must be reverified against its
 * primary source when a report is generated (`reverifyAtReport`).
 *
 * Maintenance: the owner maintains this list. Nothing reverifies it
 * automatically. Add an entry only with its check date and rule status; an
 * act that cannot be identified is never added (an unidentified act is not
 * law), and a proceeding is never recorded as an effective rule.
 *
 * `screen.ts` deliberately does not import this module.
 */
import { SOURCES_CHECKED_ON } from './types';

export const REGULATORY_CATALOG_RULE_STATUSES = [
  'effective', 'proposed', 'proceeding', 'illustrative', 'unverified-claim',
] as const;
export type RegulatoryCatalogRuleStatus = typeof REGULATORY_CATALOG_RULE_STATUSES[number];

/** Administrative review policy only; this is not a legal verification receipt.
 * The named role owns monthly catalog review and report-time primary-source
 * verification. No timer or report generation silently marks a source verified. */
export const REGULATORY_SOURCE_MAINTENANCE = Object.freeze({
  ownerRole: 'Site report owner',
  reviewCadenceDays: 30,
  lastOwnerReviewOn: SOURCES_CHECKED_ON,
  nextOwnerReviewDueOn: '2026-11-04',
  primarySourceVerifiedOn: null,
  reportTimePolicy: 'Primary-source verification required for every report; owner research alone cannot establish current law or site clearance.',
} as const);

export function regulatorySourceMaintenanceText(reportDate?: string): string {
  const due = reportDate && /^\d{4}-\d{2}-\d{2}/.test(reportDate)
    && reportDate.slice(0, 10) >= REGULATORY_SOURCE_MAINTENANCE.nextOwnerReviewDueOn;
  return `Owner research reviewed ${SOURCES_CHECKED_ON}; primary-source reverification not recorded. `
    + `Owner: ${REGULATORY_SOURCE_MAINTENANCE.ownerRole}; review every ${REGULATORY_SOURCE_MAINTENANCE.reviewCadenceDays} days, next due ${REGULATORY_SOURCE_MAINTENANCE.nextOwnerReviewDueOn}${due ? ' (due)' : ''}. `
    + REGULATORY_SOURCE_MAINTENANCE.reportTimePolicy;
}

export interface RegulatoryDoctrineSource {
  readonly id: string;
  readonly label: string;
  /** Which screen question the doctrine informs. */
  readonly topic: 'land-use' | 'air' | 'grid-interface' | 'grid-service';
  /** Geographic reach of the item. 'illustrative' items describe one place only. */
  readonly reach: 'federal' | 'single-state' | 'single-locality';
  /** Doctrine statement, phrased as attributed owner research. */
  readonly doctrine: string;
  readonly checkedOn: typeof SOURCES_CHECKED_ON;
  readonly origin: 'owner-research';
  readonly ruleStatus: RegulatoryCatalogRuleStatus;
  readonly reverifyAtReport: true;
  readonly maintenance: typeof REGULATORY_SOURCE_MAINTENANCE;
}

export const REGULATORY_DOCTRINE_SOURCES: readonly RegulatoryDoctrineSource[] = Object.freeze([
  {
    id: 'epa-2026-01-combustion-turbine-emergency-definition',
    label: 'EPA combustion-turbine rule (owner-reported January 2026) — emergency-turbine definition',
    topic: 'air',
    reach: 'federal',
    doctrine: 'Owner research reports a January 2026 EPA combustion-turbine rule that defines emergency turbines. '
      + 'Not independently verified: confirm the rule text, its status and its emergency definition against the Federal Register before relying on it. '
      + 'Reciprocating-engine emergency allowances do not transfer to turbines, and emergency approval is not prime-power permission.',
    checkedOn: SOURCES_CHECKED_ON,
    origin: 'owner-research',
    ruleStatus: 'unverified-claim',
    reverifyAtReport: true,
    maintenance: REGULATORY_SOURCE_MAINTENANCE,
  },
  {
    id: 'ferc-2026-06-18-large-load-colocation',
    label: 'FERC large-load interconnection and co-location actions (18 June 2026)',
    topic: 'grid-service',
    reach: 'federal',
    doctrine: 'FERC actions of 18 June 2026 on large-load interconnection and co-location are open proceedings. '
      + 'They are not approvals, effective tariff rights or service entitlements; check each docket and any RTO/ISO compliance filing at report time.',
    checkedOn: SOURCES_CHECKED_ON,
    origin: 'owner-research',
    ruleStatus: 'proceeding',
    reverifyAtReport: true,
    maintenance: REGULATORY_SOURCE_MAINTENANCE,
  },
  {
    id: 'ca-rule-21-non-export',
    label: 'California Rule 21 non-export interconnection (illustration)',
    topic: 'grid-interface',
    reach: 'single-state',
    doctrine: 'Illustration only: under California Rule 21, non-export (zero-injection) generation still goes through the utility interconnection review path. '
      + 'It shows that zero export does not by itself remove review; it is not a rule for sites outside the California utilities it governs.',
    checkedOn: SOURCES_CHECKED_ON,
    origin: 'owner-research',
    ruleStatus: 'illustrative',
    reverifyAtReport: true,
    maintenance: REGULATORY_SOURCE_MAINTENANCE,
  },
  {
    id: 'loudoun-county-va-2025-03-18-data-center-zoning',
    label: 'Loudoun County, VA data-center zoning action (18 March 2025, illustration)',
    topic: 'land-use',
    reach: 'single-locality',
    doctrine: 'Illustration only: owner research reports that Loudoun County, Virginia acted on 18 March 2025 to require discretionary approval for new data centers instead of by-right approval. '
      + 'It shows that local data-center use permission can change; it says nothing about any other jurisdiction.',
    checkedOn: SOURCES_CHECKED_ON,
    origin: 'owner-research',
    ruleStatus: 'illustrative',
    reverifyAtReport: true,
    maintenance: REGULATORY_SOURCE_MAINTENANCE,
  },
] as const satisfies readonly RegulatoryDoctrineSource[]);
